import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, Instagram, Facebook, Youtube, Clock, Globe, MessageCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import ConexusLogo from '@/components/logos/ConexusLogo';
import FlagIcon from '@/components/FlagIcon';
import { supabase } from '@/integrations/supabase/client';
import { trackSubscribe } from '@/utils/analytics';

const FOOTER_LANGUAGES: Array<{ code: 'en' | 'pt' | 'es' | 'fr' | 'it' | 'zh'; label: string; country: 'US' | 'BR' | 'ES' | 'FR' | 'IT' | 'CN' }> = [
  { code: 'en', label: 'English', country: 'US' },
  { code: 'pt', label: 'Português', country: 'BR' },
  { code: 'es', label: 'Español', country: 'ES' },
  { code: 'fr', label: 'Français', country: 'FR' },
  { code: 'it', label: 'Italiano', country: 'IT' },
  { code: 'zh', label: '中文', country: 'CN' },
];

const Footer = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast({ title: 'Please enter a valid email address', variant: 'destructive' });
      return;
    }

    setIsSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('newsletter-subscribe', {
        body: { email: email.trim().toLowerCase(), source: 'footer' },
      });

      if (error) throw error;

      if (data?.alreadySubscribed) {
        toast({ title: "You're already subscribed!", description: 'Thank you for your interest.' });
      } else {
        trackSubscribe();
        toast({ title: 'Thank you for subscribing!', description: "We'll keep you updated with the latest news." });
      }
      setEmail('');
    } catch (err) {
      console.error('Newsletter subscribe error:', err);
      toast({ title: 'Something went wrong', description: 'Please try again later.', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const quickLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/services', label: t('nav.services') },
    { path: '/programs', label: t('nav.programs') },
    { path: '/stories', label: t('nav.stories') },
    { path: '/resources', label: t('nav.resources') },
    { path: '/contact', label: t('nav.contact') },
  ];

  return (
    <footer className="bg-[#0A192F] text-white">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="space-y-4">
            <ConexusLogo className="h-10 w-auto" variant="full" invertColors />
            <p className="text-white/80 text-sm leading-relaxed">
              {t('footer.tagline')}
            </p>
            <div className="flex gap-3 pt-4">
              <a href="https://www.instagram.com/conexusglobalconsulting" target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors" aria-label="Instagram">
                <Instagram className="h-5 w-5" />
              </a>
              <a href="https://www.tiktok.com/@conexusglobalconsulting" target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors" aria-label="TikTok">
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.1a8.16 8.16 0 0 0 4.76 1.52v-3.4c-.86 0-1.7-.18-2.47-.53h-.53Z"/></svg>
              </a>
              <a href="https://www.facebook.com/conexusglobalconsulting" target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors" aria-label="Facebook">
                <Facebook className="h-5 w-5" />
              </a>
              <a href="https://www.youtube.com/@conexusglobalconsulting" target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors" aria-label="YouTube">
                <Youtube className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-4">{t('footer.quickLinks')}</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-white/80 hover:text-primary transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-semibold mb-4">{t('nav.contact')}</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Mail className="h-5 w-5 mt-0.5 text-primary flex-shrink-0" />
                <div className="space-y-1">
                  <a href="mailto:info@conexusglobalconsulting.com" className="text-white/80 hover:text-primary transition-colors text-sm block">
                    info@conexusglobalconsulting.com
                  </a>
                  <a href="mailto:partners@conexusglobalconsulting.com" className="text-white/80 hover:text-primary transition-colors text-sm block">
                    partners@conexusglobalconsulting.com
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="h-5 w-5 mt-0.5 text-primary flex-shrink-0" />
                <div className="space-y-1">
                  <a href="tel:+14078190008" className="text-white/80 hover:text-primary transition-colors text-sm block">
                    {t('contact.info.call')}: +1 (407) 819-0008
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MessageCircle className="h-5 w-5 mt-0.5 text-primary flex-shrink-0" />
                <a href="https://wa.me/16892317017" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-primary transition-colors text-sm">
                  WhatsApp: +1 (689) 231-7017
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="h-5 w-5 mt-0.5 text-primary flex-shrink-0" />
                <span className="text-white/80 text-sm">
                  Mon - Fri: 11AM - 5:30PM EST
                </span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 mt-0.5 text-primary flex-shrink-0" />
                <span className="text-white/80 text-sm">
                  4700 Millenia Blvd, Suite 500,<br />
                  Orlando, FL 32839
                </span>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-semibold mb-4">{t('footer.newsletter.title')}</h4>
            <form onSubmit={handleSubscribe} className="space-y-3">
              <Input
                type="email"
                placeholder={t('footer.newsletter.placeholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                required
              />
              <Button 
                type="submit" 
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Subscribing...' : t('footer.newsletter.subscribe')}
              </Button>
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/20 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/60 text-sm">
            © {currentYear} Conexus Global Consulting. All rights reserved.
          </p>

          {/* Language switcher */}
          <div
            role="group"
            aria-label={t("common.selectLanguage")}
            className="flex items-center gap-1 rounded-full border border-white/20 bg-white/5 p-1"
          >
            <Globe className="h-3.5 w-3.5 text-white/60 ml-1.5 mr-0.5" aria-hidden="true" />
            {FOOTER_LANGUAGES.map((lang) => {
              const active = i18n.language === lang.code;
              return (
                <button
                  key={lang.code}
                  onClick={() => i18n.changeLanguage(lang.code)}
                  aria-pressed={active}
                  aria-label={`Switch to ${lang.label}`}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-all ${
                    active
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <FlagIcon country={lang.country} className="h-2.5 w-[15px] rounded-sm" />
                  <span className="uppercase tracking-wide">{lang.code}</span>
                </button>
              );
            })}
          </div>

          <div className="flex gap-6">
            <Link to="/privacy" className="text-white/60 hover:text-white text-sm transition-colors">
              {t('footer.privacy')}
            </Link>
            <Link to="/terms" className="text-white/60 hover:text-white text-sm transition-colors">
              {t('footer.terms')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
